import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { useSeo } from "@/lib/useSeo";
import { renderEmphasis } from "@/lib/richText";

const HERO_IMG = "https://images.unsplash.com/photo-1518156677180-95a2893f3e9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxjYWxtJTIwb2NlYW4lMjB3YXZlcyUyMG1pbmltYWxpc3R8ZW58MHx8fHwxNzgyMjk4OTI3fDA&ixlib=rb-4.1.0&q=85";
const DEFAULT_PORTRAIT = "https://m.media-amazon.com/images/S/amzn-author-media-prod/lmoep5q2ag58vik7m0od2osik4.jpg";

export default function Home() {
  const [books, setBooks] = useState([]);
  const [posts, setPosts] = useState([]);
  const [portrait, setPortrait] = useState(DEFAULT_PORTRAIT);
  const [heroImg, setHeroImg] = useState(HERO_IMG);
  const [content, setContent] = useState({});

  useSeo({
    title: null, // home uses default site title
    description: "Gemma Veda — new-age author of the Soul's CODE series. Stories drawn from experience, culture and ancient wisdom.",
    image: heroImg,
  });

  useEffect(() => {
    api.get("/books").then(({ data }) => setBooks(data)).catch(() => {});
    api.get("/posts").then(({ data }) => setPosts(data)).catch(() => {});
    api.get("/settings").then(({ data }) => {
      if (data.portrait_url) setPortrait(data.portrait_url);
      if (data.hero_image_url) setHeroImg(data.hero_image_url);
      setContent(data);
    }).catch(() => {});
  }, []);

  const featured = books.find((b) => b.featured) || books[0];

  return (
    <PublicLayout>
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="hero-ocean relative min-h-[92vh] flex items-end overflow-hidden"
        style={{
          backgroundImage: `url(${heroImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-40 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="gv-divider bg-[#E8D8B7]" style={{ background: "#E8D8B7" }} />
              <span className="text-xs tracking-[0.32em] uppercase text-[#E8D8B7]" data-testid="home-hero-tagline">
                {content.home_hero_tagline || "Author · Storyteller · Soul Seeker"}
              </span>
            </div>
            <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-3xl" data-testid="home-hero-heading">
              {renderEmphasis(content.home_hero_heading || "Stories drawn from *experience*, culture and ancient wisdom.", "text-[#E8D8B7]")}
            </h1>
            <p className="mt-6 text-white/85 max-w-xl text-lg leading-relaxed font-light" data-testid="home-hero-subtext">
              {content.home_hero_subtext || "The literary home of Gemma Veda — author of the Soul's CODE series: a journey to soul searching, a tapestry of awakening."}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/books" data-testid="hero-cta-books" className="gv-btn-gold">Explore the Books</Link>
              <Link to="/journal" data-testid="hero-cta-journal" className="text-white text-sm tracking-[0.2em] uppercase border-b border-[#E8D8B7] pb-1 hover:text-[#E8D8B7] transition-colors">Read the Journal →</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section data-testid="home-about" className="py-24 md:py-32 px-6 md:px-12 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="md:col-span-5"
          >
            <div className="relative">
              <img src={portrait} alt="Gemma Veda" className="w-full h-[560px] object-cover" data-testid="home-portrait" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-[#C5A059] hidden md:block" />
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="md:col-span-7"
          >
            <div className="gv-label">About the Author</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 text-[#1A1F24] leading-tight" data-testid="home-about-heading">
              {content.home_about_heading || "A new-age writer keeping company with the rhythm of life."}
            </h2>
            <div className="mt-8 max-w-xl text-[#1A1F24] gv-prose">
              <p data-testid="home-about-paragraph1">
                {content.home_about_paragraph1 || "Gemma Veda is a new-age author and writer whose stories are inspired by the rhythm of life and ancient wisdom."}
              </p>
              <p style={{ fontSize: "1rem" }} className="text-[#5C6670]" data-testid="home-about-paragraph2">
                {content.home_about_paragraph2 || "Her books draw insight from experience, culture and heritage to weave tales that resonate at a deep level. When she isn't writing, she loves exploring new places and pursuing different hobbies. Discover more through her debut book — Not A Silent Letter — and the ongoing series, Soul's CODE: A Journey to Soul Searching, A Tapestry of Awakening."}
              </p>
            </div>
            <div className="mt-8">
              <Link to="/contact" className="gv-link text-sm tracking-[0.15em] uppercase">Write to Gemma</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURED BOOK */}
      {featured && (
        <section data-testid="home-featured-book" className="py-24 md:py-32 px-6 md:px-12 bg-white border-y border-[#E5E5E5]">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="md:col-span-5"
            >
              <img src={featured.cover_image} alt={featured.title} className="w-full max-w-sm mx-auto shadow-2xl" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="md:col-span-7"
            >
              <div className="gv-label">Latest Work</div>
              <h2 className="font-serif text-4xl md:text-5xl mt-4 text-[#1A1F24] leading-tight italic">
                {featured.title}
              </h2>
              {featured.subtitle && (
                <p className="mt-2 text-[#5C6670] text-lg font-light tracking-wide">— {featured.subtitle}</p>
              )}
              <p className="mt-6 text-[#1A1F24] text-lg leading-relaxed font-light max-w-xl">
                {featured.description}
              </p>
              <div className="mt-8 flex gap-4 items-center">
                <Link to="/books" className="gv-btn-gold" data-testid="featured-book-explore">All Books</Link>
                {featured.buy_link && featured.buy_link !== "#" && (
                  <a href={featured.buy_link} target="_blank" rel="noopener noreferrer" className="gv-link text-sm tracking-[0.15em] uppercase">Buy →</a>
                )}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* JOURNAL TEASE */}
      {posts.length > 0 && (
      <section data-testid="home-journal" className="py-24 md:py-32 px-6 md:px-12 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-16">
            <div>
              <div className="gv-label">From the Journal</div>
              <h2 className="font-serif text-4xl md:text-5xl mt-3 text-[#1A1F24]">Recent reflections</h2>
            </div>
            <Link to="/journal" className="gv-link text-sm tracking-[0.15em] uppercase" data-testid="journal-view-all">View all entries</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            {posts.slice(0, 3).map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="hover-lift"
              >
                <Link to={`/journal/${p.slug}`} data-testid={`home-post-${p.slug}`} className="block group">
                  <div className="aspect-[4/5] overflow-hidden bg-[#E5E5E5]">
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="pt-6">
                    <div className="text-xs tracking-[0.2em] uppercase text-[#C5A059]">{p.tag || "Essay"}</div>
                    <h3 className="font-serif text-2xl md:text-3xl mt-3 text-[#1A1F24] leading-tight group-hover:text-[#A68545] transition-colors">{p.title}</h3>
                    <p className="mt-3 text-[#5C6670] text-base font-light leading-relaxed line-clamp-3">{p.excerpt}</p>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
      )}
    </PublicLayout>
  );
}
